import { inject, Injectable } from '@angular/core';
import {
  ClientInvoiceDraftItem,
  ClientInvoiceDraftItemService,
  ClientOrderItem,
  ClientOrderItemService,
  findSectionIriByEntityIri,
  RouteEntitySubscriberService,
  rowFirst,
  setSectionValues,
  SupplierInvoice,
  SupplierInvoiceItem,
  SupplierInvoiceItemMapping,
  SupplierInvoiceItemMappingService,
  SupplierInvoiceItemService,
  SupplierInvoiceSection,
  SupplierInvoiceSectionService,
  SupplierInvoiceService,
  upsertInSection,
} from 'shared';
import { forkJoin, Subscription, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SupplierInvoiceCoordinator {
  // DI
  #tracker = inject(RouteEntitySubscriberService);

  // Every refresh below is triggered by a mercure topic and fetches by IRI captured at
  // call time. Without tracking + cancelling them, a refresh started for the invoice
  // you're leaving (e.g. its lock-release echo) can land after you've already navigated
  // to a different invoice and overwrite that invoice's data with the old one's.
  #pendingRefreshes = new Subscription();
  cancelPendingRefreshes(): void {
    this.#pendingRefreshes.unsubscribe();
    this.#pendingRefreshes = new Subscription();
  }

  #invoiceSvc = inject(SupplierInvoiceService);
  #sectionsSvc = inject(SupplierInvoiceSectionService);
  #itemsSvc = inject(SupplierInvoiceItemService);
  #mappingsSvc = inject(SupplierInvoiceItemMappingService);
  #linkedSvc = inject(ClientOrderItemService);
  #draftsSvc = inject(ClientInvoiceDraftItemService);

  // Signals (from tracker)
  #invoiceSig = this.#tracker.getSignal<SupplierInvoice>('supplierInvoice');
  #sectionsSig = this.#tracker.getSectionsSignals<SupplierInvoiceSection>('supplierInvoice');
  #itemsBySectionSig = this.#tracker.getSectionItemsSignal<SupplierInvoiceItem>('supplierInvoice');
  #mappingsBySectionSig = this.#tracker.getItemMappingsSignal<SupplierInvoiceItemMapping>('supplierInvoice');
  #linkedBySectionSig = this.#tracker.getLinkedItemsSignal<ClientOrderItem>('supplierInvoice');
  #draftsBySectionSig = this.#tracker.getDraftItemsSignal<ClientInvoiceDraftItem>('supplierInvoice');

  // Optional hook provided by the component to recompute mappedAmount
  #recompute?: (sectionIri: string) => void;
  setRecompute(fn: (sectionIri: string) => void) {
    this.#recompute = fn;
  }

  // Helpers
  #invoiceIri(): string | undefined {
    return this.#invoiceSig()?.['@id'];
  }

  // -------------------- Public refresh API --------------------

  /** Refresh the invoice entity (also covers totals/status if they live on the entity). */
  refreshInvoiceEntity(): void {
    const iri = this.#invoiceIri();
    if (!iri) return;
    this.#pendingRefreshes.add(
      this.#invoiceSvc
        .get(iri)
        .pipe(take(1))
        .subscribe((inv) => this.#invoiceSig.set(inv)),
    );
  }

  /** Historically you called updateTotals(); alias it to the entity fetch to keep behavior. */
  refreshTotals(): void {
    this.refreshInvoiceEntity();
  }

  /** Refresh the sections list for this invoice. */
  refreshSectionsList(): void {
    const iri = this.#invoiceIri();
    if (!iri) return;
    this.#pendingRefreshes.add(
      this.#sectionsSvc
        .getForSupplierInvoice(iri)
        .pipe(take(1))
        .subscribe((sections) => this.#sectionsSig.set(sections)),
    );
  }

  /** Refresh a full section slice: items, mappings, linked, drafts. */
  refreshSection(sectionIri: string): void {
    const one = [sectionIri];
    this.#pendingRefreshes.add(
      forkJoin({
        items: this.#itemsSvc.getForSupplierInvoiceSection(one).pipe(take(1)),
        mappings: this.#mappingsSvc.getForSupplierInvoiceSection(one).pipe(take(1)),
        linked: this.#linkedSvc.getForSupplierInvoiceSection(one).pipe(take(1)),
        drafts: this.#draftsSvc.getForSupplierInvoiceSection(one).pipe(take(1)),
      })
        .pipe(take(1))
        .subscribe(({ items, mappings, linked, drafts }) => {
          this.#itemsBySectionSig.update((s) => setSectionValues(s, sectionIri, items[sectionIri] ?? []));
          this.#mappingsBySectionSig.update((s) => setSectionValues(s, sectionIri, mappings[sectionIri] ?? []));
          this.#linkedBySectionSig.update((s) => setSectionValues(s, sectionIri, linked[sectionIri] ?? []));
          this.#draftsBySectionSig.update((s) => setSectionValues(s, sectionIri, drafts[sectionIri] ?? []));
          this.#recompute?.(sectionIri);
        }),
    );
  }

  /** Refresh a single item by IRI, or fall back to section if we can’t resolve. */
  refreshSectionItem(itemIri: string): void {
    const bySection = this.#itemsBySectionSig();
    const sectionIri = findSectionIriByEntityIri(bySection, itemIri) ?? undefined;
    if(sectionIri)
    this.refreshSectionMappings(sectionIri);

    if (!sectionIri) {
      this.refreshSectionsList();

      return;
    }

    rowFirst<SupplierInvoiceItem>(
      (iri) => (this.#itemsSvc as any).get(iri),
      itemIri,
      (fresh) => upsertInSection(this.#itemsBySectionSig, sectionIri, fresh),
      () => this.refreshSection(sectionIri),
    );
  }

  /** Refresh all mappings for a section. */
  refreshSectionMappings(sectionIri: string): void {
    this.#pendingRefreshes.add(
      this.#mappingsSvc
        .getForSupplierInvoiceSection([sectionIri])
        .pipe(take(1))
        .subscribe((bySec) => {
          this.#mappingsBySectionSig.update((s) => setSectionValues(s, sectionIri, bySec[sectionIri] ?? []));
          this.#recompute?.(sectionIri);
        }),
    );
  }

  /** Refresh only the mappings for a single invoice line (leave others intact). */
  refreshItemMappingForLine(sectionIri: string, itemIri: string): void {
    this.#pendingRefreshes.add(
      this.#mappingsSvc
        .getForSupplierInvoiceItem(itemIri)
        .pipe(take(1))
        .subscribe(
          (list) => {
            this.#mappingsBySectionSig.update((sections) => {
              const current = (sections?.[sectionIri] ?? []) as SupplierInvoiceItemMapping[];
              const kept = current.filter((m) => m.supplierInvoiceItem !== itemIri);
              return setSectionValues(sections, sectionIri, [...kept, ...list]);
            });
            this.#recompute?.(sectionIri);
          },
          (_) => this.refreshSectionMappings(sectionIri),
        ),
    );
  }

  /** Refresh linked client order items for a section. */
  refreshSectionLinkedItems(sectionIri: string): void {
    this.#pendingRefreshes.add(
      this.#linkedSvc
        .getForSupplierInvoiceSection([sectionIri])
        .pipe(take(1))
        .subscribe((bySec) => {
          this.#linkedBySectionSig.update((s) => setSectionValues(s, sectionIri, bySec[sectionIri] ?? []));
          this.#recompute?.(sectionIri);
        }),
    );
  }

  /** Refresh draft items for a section (replace slice). */
  refreshSectionDraftItems(sectionIri: string): void {
    this.#pendingRefreshes.add(
      this.#draftsSvc
        .getForSupplierInvoiceSection([sectionIri])
        .pipe(take(1))
        .subscribe((bySec) => {
          this.#draftsBySectionSig.update((s) => setSectionValues(s, sectionIri, bySec[sectionIri] ?? []));
          this.#recompute?.(sectionIri);
        }),
    );
  }

  /** Explicit topic to force reload drafts slice. */
  forceReloadDraftItems(sectionIri: string): void {
    this.refreshSectionDraftItems(sectionIri);
  }
}
